import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router-dom';

// import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import { brandText } from '../../../../config/branding';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import dashboard from '../../../../utils/api/dashboard';

interface PenaltyRevenueChartProperties {
  startDate: string;
  endDate: string;
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * PenaltyRevenueChart component
 * Visualizes penalty revenue over time using a smooth area chart
 * @param {any} param0 - The param0
 * @param {any} param0.startDate - The startDate prop
 * @param {any} param0.endDate - The endDate prop
 * @param {any} param0.filters - The filters prop
 */
function PenaltyRevenueChart({ startDate, endDate, filters }: PenaltyRevenueChartProperties) {
  console.log(filters, 'filters');
  const { t } = useTranslation('dashboard');
  // const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState<any>({
    labels: [],
    datasets: [],
  });

  /**
   * Handles navigation to the penalty management overrides page.
   */
  // const handleNavigate = () => {
  //   navigate('../penalty-management/overrides');
  // };
  /**
   * Fetches penalty revenue data from the API and updates chart state.
   */
  const fetchPenaltyRevenue = async () => {
    setIsLoading(true);
    try {
      const { type, ...otherFilters } = filters || {};
      const response = await dashboard.getPenaltyRevenue({
        startDate,
        endDate,
        ...otherFilters,
        type,
      });
      if (response?.status === 200 && Array.isArray(response?.data)) {
        const labels = response.data.map((item: any) => item.label);
        const penaltyRevenueData = response.data.map((item: any) => item.penaltyRevenue);

        setChartData({
          labels,
          datasets: [
            {
              label: t('penaltyRevenue'),
              data: penaltyRevenueData,
              fill: true,
              borderColor: '#F79009',
              /**
               * Sets the background color using a linear gradient.
               * @param {any} context - The chart context.
               * @returns {any} The linear gradient object or null.
               */
              backgroundColor: (context: any) => {
                const { chart } = context;
                const { ctx, chartArea } = chart;
                if (!chartArea) return null;
                const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                gradient.addColorStop(0, 'rgba(247, 144, 9, 0.4)');
                gradient.addColorStop(1, 'rgba(247, 144, 9, 0)');
                return gradient;
              },
              tension: 0.4,
              pointRadius: 0,
              borderWidth: 2,
            },
          ],
        });
      }
    } catch (error) {
      console.error('Error fetching penalty revenue data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  /**
   * Effect to reload data when filters change.
   */
  useEffect(() => {
    fetchPenaltyRevenue();
  }, [filters, startDate, endDate]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
      },
      tooltip: {
        /**
         * Function to create tooltip bg color
         * @param {any} context - The context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          if (context?.tooltipItems?.[0]?.dataset) {
            if (context.tooltipItems[0].dataset.label === t('penaltyRevenue')) {
              return 'rgba(247, 144, 9, 0.9)';
            }
          }
          return 'rgba(0, 0, 0, 0.8)';
        },
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Function to generate title for chart tooltip
           * @param {any} context - The context
           * @returns {any} The result
           */
          title: (context: any) => {
            const datasetLabel = context[0]?.dataset?.label;
            return datasetLabel;
          },
          /**
           * Function to show tooltip data
           * @param {any} context - The context
           * @returns {any} The result
           */
          label: (context: any) => {
            const datasetLabel = context?.dataset?.label;
            return `${context?.raw.toLocaleString()}  ${datasetLabel === t('penaltyRevenue') ? brandText.currency : ''}`;
          },
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: true,
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
        },
        border: {
          display: true,
        },
      },
    },
  };

  return (
    <div className="penalty-revenue-chart-wrap">
      <div className="penalty-revenue-chart-header">
        <div className="title-wrapper">
          <h6>{t('penaltyRevenue')}</h6>
          <TooltipIcon text="Penalty revenue chart" infoColor="info" />
        </div>
        {/* <NavigateArrow className="header-arrow" onClick={handleNavigate} /> */}
      </div>
      <div className="penalty-revenue-chart-container">
        {isLoading ? (
          <div className="battery-loader-wrap">
            <BatteryLoader />
          </div>
        ) : (
          <Line data={chartData} options={options} />
        )}
      </div>
    </div>
  );
}

PenaltyRevenueChart.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};

export default PenaltyRevenueChart;
